// Schedule Constraints — editable constraint manager.
// Sections (top → bottom):
//   1. Provider Availability (unchanged)
//   2. School (per-school name + AM/PM/Full Day default attendance windows)
//   3. Grade Schedules — per-grade cards with AM | PM | Full Day sub-tabs.
//      Each sub-tab carries its own attendance window + constraint blocks
//      (lunch / recess / specials / other). Wizard reads the slot matching
//      the student's attendance_pattern.
//   4. Teacher-Dependent Constraints (Iter 33) — overrides scoped by
//      teacher name (e.g. Grade K recess differs by classroom).
//   5. Flex Days (unchanged)
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Trash, FloppyDisk, X, Calendar, Sun, Pencil, Eye, EyeSlash, Chalkboard } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { nativeOptions } from "@/lib/nativeOptions";
import { DayOfWeekMultiPicker, expandDays } from "@/components/schedule/DayOfWeekMultiPicker";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri"];
// FP3-035B correction — Kindergarten = "0" platform-wide.
const GRADE_OPTIONS = Array.from({ length: 13 }, (_, i) => String(i));
const KIND_OPTIONS = ["lunch", "recess", "specials", "other"];
const PATTERN_KEYS = ["am", "pm", "full_day"];
const PATTERN_LABEL = { am: "AM", pm: "PM", full_day: "Full Day" };
const PATTERN_DEFAULT_WINDOW = {
  am: { start: "08:00", end: "11:30" },
  pm: { start: "12:30", end: "15:00" },
  full_day: { start: "08:00", end: "15:00" },
};

// Iter 30 split: this page is now the **School Constraints** view.
// Provider availability moved out into the dedicated ProviderConstraints
// page. The default export name remains ScheduleConstraints so existing
// imports/tests continue to work — the named export below makes the new
// product naming explicit at the import site.
export default function ScheduleConstraints() {
  const navigate = useNavigate();
  const [schools, setSchools] = useState([]);
  const [activeSchoolId, setActiveSchoolId] = useState(null);
  const [gradeSchedules, setGradeSchedules] = useState([]);
  const [teacherConstraints, setTeacherConstraints] = useState([]);
  const [flex, setFlex] = useState([]);
  const [sessionExceptions, setSessionExceptions] = useState([]);
  const [loading, setLoading] = useState(true);
  // Iter 33 — Hidden grades, persisted per-school in localStorage so the
  // declutter choice survives reloads. Stored as { [schoolId]: string[] }.
  const [hiddenGrades, setHiddenGrades] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("frame.school_rules.hidden_grades") || "{}");
    } catch {
      return {};
    }
  });
  const persistHidden = (next) => {
    setHiddenGrades(next);
    try {
      localStorage.setItem("frame.school_rules.hidden_grades", JSON.stringify(next));
    } catch {
      /* localStorage unavailable — non-fatal */
    }
  };
  const toggleGradeHidden = (grade) => {
    if (!activeSchoolId) return;
    const current = hiddenGrades[activeSchoolId] || [];
    const next = current.includes(grade)
      ? current.filter((g) => g !== grade)
      : [...current, grade];
    persistHidden({ ...hiddenGrades, [activeSchoolId]: next });
  };
  const showAllGrades = () => {
    if (!activeSchoolId) return;
    persistHidden({ ...hiddenGrades, [activeSchoolId]: [] });
  };

  // Iter 33d Follow-up 4 — `fetchAll` does the actual data fetch.
  // `load()` is the initial blocking load (shows the skeleton).
  // `silentRefresh()` is what every `onSaved` callback now uses — it
  // refetches in the background WITHOUT flipping `loading=true`, so
  // the grade grid (and any focused grade card, expanded section,
  // etc.) stays mounted across the save. This is what makes the save
  // feel quiet and in-place instead of "booting" the user out.
  const fetchAll = async () => {
    const [s, gs, tc, f, se] = await Promise.all([
      api.listSchools(),
      api.listGradeSchedules(),
      api.listTeacherConstraints(),
      api.listFlexDays(),
      api.listSessionExceptions(),
    ]);
    setSchools(s.data);
    setGradeSchedules(gs.data);
    setTeacherConstraints(tc.data);
    setFlex(f.data);
    setSessionExceptions(se.data);
    if (!activeSchoolId && s.data.length > 0) setActiveSchoolId(s.data[0].id);
  };
  const load = async () => {
    setLoading(true);
    try {
      await fetchAll();
    } catch (e) {
      toast.error(`Failed to load constraints: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };
  const silentRefresh = async () => {
    try {
      await fetchAll();
    } catch (e) {
      // Don't toast on a silent refresh — keep the post-save UX calm.
      // The originating action already toasted its own success/failure.
      console.warn("silent refresh failed", e);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredFlex = flex.filter((f) => !activeSchoolId || f.school_id === activeSchoolId);
  const filteredExceptions = sessionExceptions.filter(
    (e) => !e.school_id || !activeSchoolId || e.school_id === activeSchoolId
  );
  const activeHiddenGrades = (activeSchoolId && hiddenGrades[activeSchoolId]) || [];
  const visibleGrades = GRADE_OPTIONS.filter((g) => !activeHiddenGrades.includes(g));

  return (
    <div className="space-y-5" data-testid="school-constraints">
      {/* School */}
      <Card className="bg-white rounded-2xl border border-[#E5E3DB] shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold text-[#1F2937] heading-font">
            School
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* FP3-051 — Empty-state CTA. Solo providers (and any org
              with zero schools) cannot meaningfully use school rules
              until they add a campus first. Link directly to the
              place that can add one. */}
          {schools.length === 0 && (
            <div
              data-testid="school-rules-empty-state"
              className="rounded-md bg-[#F7F5EF] border border-[#E5E3DB] p-4"
            >
              <p className="text-[12.5px] text-[#4B5563] mb-3">
                Add a school or workspace before setting school rules.
                School rules, grade schedules, constraints, and the
                schedule builder all key off a campus.
              </p>
              <Button
                data-testid="school-rules-add-school-cta"
                onClick={() => navigate("/account/workspace")}
                size="sm"
                className="bg-[#1E3F33] hover:bg-[#173127] text-white"
              >
                <Plus size={12} weight="bold" className="mr-1.5" />
                Add school / workspace
              </Button>
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            {schools.map((s) => {
              const active = s.id === activeSchoolId;
              return (
                <button
                  key={s.id}
                  onClick={() => setActiveSchoolId(s.id)}
                  data-testid={`school-tab-${s.id}`}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                    active
                      ? "bg-[#1E3F33] text-white border-[#1E3F33]"
                      : "bg-white text-[#4B5563] border-[#d4d2cc] hover:bg-[#F3F1EC]"
                  }`}
                >
                  {s.name}
                </button>
              );
            })}
          </div>
          {activeSchoolId && (
            <SchoolEditor
              key={activeSchoolId}
              school={schools.find((s) => s.id === activeSchoolId)}
              onSaved={silentRefresh}
            />
          )}
        </CardContent>
      </Card>

      {/* Grade Schedules */}
      <Card className="bg-white rounded-2xl border border-[#E5E3DB] shadow-sm">
        <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base font-semibold text-[#1F2937] heading-font flex items-center gap-2">
            <Calendar size={16} weight="duotone" className="text-[#1E3F33]" />
            Grade Schedules
          </CardTitle>
          <p className="text-[10px] text-[#6B7280] italic">
            Each grade has separate AM, PM, and Full Day schedules used by the wizard.
          </p>
        </CardHeader>
        <CardContent>
          {/* Iter 33 — Hide/show grades. Click a grade chip to toggle.
              Hidden grades stay in the data; only their card is removed
              from this view to declutter. Persists per-school in
              localStorage. */}
          {activeSchoolId && (
            <div
              className="mb-3 pb-3 border-b border-[#ECEAE4]"
              data-testid="grade-visibility-bar"
            >
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-[#4B5563]">
                  Visible grades
                </p>
                {activeHiddenGrades.length > 0 && (
                  <button
                    onClick={showAllGrades}
                    data-testid="grade-visibility-show-all"
                    className="text-[10px] font-semibold text-[#1E3F33] hover:text-[#173127] flex items-center gap-1"
                  >
                    <Eye size={10} weight="duotone" /> Show all
                  </button>
                )}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {GRADE_OPTIONS.map((g) => {
                  const hidden = activeHiddenGrades.includes(g);
                  return (
                    <button
                      key={g}
                      onClick={() => toggleGradeHidden(g)}
                      data-testid={`grade-visibility-${g}`}
                      title={hidden ? `Show Grade ${g}` : `Hide Grade ${g}`}
                      className={`px-2 py-1 rounded-md text-[11px] font-semibold border transition-colors flex items-center gap-1 ${
                        hidden
                          ? "bg-[#F3F1EC] text-[#9B9890] border-[#E5E3DB] line-through"
                          : "bg-[#1E3F33] text-white border-[#1E3F33] hover:bg-[#173127]"
                      }`}
                    >
                      {hidden ? <EyeSlash size={10} /> : <Eye size={10} />}
                      <span>Gr {g}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {loading ? (
            <p className="text-xs text-[#6B7280]">Loading…</p>
          ) : (
            <GradeScheduleGrid
              schoolId={activeSchoolId}
              school={schools.find((s) => s.id === activeSchoolId)}
              gradeSchedules={gradeSchedules.filter((g) => g.school_id === activeSchoolId)}
              visibleGrades={visibleGrades}
              onSaved={silentRefresh}
            />
          )}
        </CardContent>
      </Card>

      {/* Teacher-Dependent Constraints — Iter 33 */}
      <Card className="bg-white rounded-2xl border border-[#E5E3DB] shadow-sm">
        <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base font-semibold text-[#1F2937] heading-font flex items-center gap-2">
            <Chalkboard size={16} weight="duotone" className="text-[#4A6B8C]" />
            Teacher-Dependent Constraints
            <span className="text-[10px] font-normal text-[#6B7280]">
              ({teacherConstraints.filter((t) => t.school_id === activeSchoolId).length})
            </span>
          </CardTitle>
          <p className="text-[10px] text-[#6B7280] italic">
            Use when a block (recess, lunch, specials) varies by teacher rather than by grade.
          </p>
        </CardHeader>
        <CardContent>
          <TeacherConstraintsCard
            schoolId={activeSchoolId}
            constraints={teacherConstraints.filter((t) => t.school_id === activeSchoolId)}
            onSaved={silentRefresh}
          />
        </CardContent>
      </Card>

      {/* Calendar Exceptions — Iter 33b */}
      <Card className="bg-white rounded-2xl border border-[#E5E3DB] shadow-sm">
        <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base font-semibold text-[#1F2937] heading-font flex items-center gap-2">
            <Calendar size={16} weight="duotone" className="text-[#B85C5C]" />
            Calendar Exceptions ({filteredExceptions.length})
          </CardTitle>
          <p className="text-[10px] text-[#6B7280] italic">
            Holidays, school breaks, and one-off closures. Date-based — sessions skip automatically.
          </p>
        </CardHeader>
        <CardContent>
          <SessionExceptionsCard
            schoolId={activeSchoolId}
            schools={schools}
            exceptions={filteredExceptions}
            onSaved={silentRefresh}
          />
        </CardContent>
      </Card>

      {/* Flex days */}
      <Card className="bg-white rounded-2xl border border-[#E5E3DB] shadow-sm">
        <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base font-semibold text-[#1F2937] heading-font flex items-center gap-2">
            <Sun size={16} weight="duotone" className="text-[#D9A05B]" />
            Flex Days ({filteredFlex.length})
          </CardTitle>
          <AddFlexDayButton schoolId={activeSchoolId} onCreated={silentRefresh} />
        </CardHeader>
        <CardContent>
          {filteredFlex.length === 0 ? (
            <p className="text-xs text-[#6B7280] italic">
              No flex days configured. Add half-day Wednesdays, holiday closures, etc.
            </p>
          ) : (
            <div className="border border-[#E5E3DB] rounded-lg overflow-hidden">
              <div className="grid grid-cols-[100px_120px_180px_1fr_70px_70px] gap-2 px-3 py-2 bg-[#F3F1EC] text-[10px] font-semibold uppercase tracking-wider text-[#4B5563]">
                <span>Kind</span>
                <span>When</span>
                <span>Window / Status</span>
                <span>Label</span>
                <span>Closed</span>
                <span></span>
              </div>
              <div className="divide-y divide-[#ECEAE4]">
                {filteredFlex.map((f) => (
                  <FlexDayRow key={f.id} f={f} onSaved={silentRefresh} />
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// School editor — name + AM, PM, Full Day default windows.
// ──────────────────────────────────────────────────────────────
function SchoolEditor({ school, onSaved }) {
  const initial = () => ({
    name: school?.name || "",
    am: school?.attendance_window_am || PATTERN_DEFAULT_WINDOW.am,
    pm: school?.attendance_window_pm || PATTERN_DEFAULT_WINDOW.pm,
    full_day: school?.attendance_window_full_day || PATTERN_DEFAULT_WINDOW.full_day,
  });
  const [draft, setDraft] = useState(initial);

  const save = async () => {
    try {
      await api.updateSchool(school.id, {
        name: draft.name,
        attendance_window_am: draft.am,
        attendance_window_pm: draft.pm,
        attendance_window_full_day: draft.full_day,
      });
      toast.success("School saved");
      onSaved();
    } catch (e) {
      toast.error(`Save failed: ${e.message}`);
    }
  };
  if (!school) return null;
  return (
    <div className="grid gap-3 min-w-0" data-testid={`school-editor-${school.id}`}>
      {/* FP3-075F — Responsive layout. The previous
          `md:grid-cols-[1fr_auto_auto_auto]` packed Name + 3 time-window
          pairs into a single row, which overflowed the card (and
          pushed the Save button off-page) any time the sidebar was
          open + the browser was narrower than ~1280px. We now stack
          on small/medium, go 2-col on lg, and only fall back to the
          tight 4-col rail on xl+. `min-w-0` lets children shrink. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-[1fr_auto_auto_auto] gap-3 items-end min-w-0">
        <Field label="Name">
          <Input
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            className="h-8 text-xs"
            data-testid="school-name-input"
          />
        </Field>
        <WindowField
          label="AM window"
          value={draft.am}
          testidPrefix="school-am"
          onChange={(v) => setDraft({ ...draft, am: v })}
        />
        <WindowField
          label="PM window"
          value={draft.pm}
          testidPrefix="school-pm"
          onChange={(v) => setDraft({ ...draft, pm: v })}
        />
        <WindowField
          label="Full Day window"
          value={draft.full_day}
          testidPrefix="school-fullday"
          onChange={(v) => setDraft({ ...draft, full_day: v })}
        />
      </div>
      <div className="flex justify-end">
        <Button
          size="sm"
          onClick={save}
          data-testid="school-save"
          className="bg-[#1E3F33] hover:bg-[#173127] text-white rounded-lg h-8 text-xs"
        >
          <FloppyDisk size={12} weight="bold" className="mr-1" />
          Save school
        </Button>
      </div>
    </div>
  );
}

function WindowField({ label, value, onChange, testidPrefix }) {
  return (
    <Field label={label}>
      {/* FP3-075F — Allow the two time inputs to wrap when the parent
          column is narrower than ~260px. `flex-wrap` plus `min-w-0`
          on the wrapper ensures the dash stays adjacent to the start
          input and the end input drops to a new line instead of
          spilling out of the card. */}
      <div className="flex flex-wrap items-center gap-1 min-w-0">
        <Input
          type="time"
          value={value?.start || ""}
          onChange={(e) => onChange({ ...value, start: e.target.value })}
          data-testid={`${testidPrefix}-start`}
          className="h-8 text-xs w-[120px] min-w-0"
        />
        <span className="text-[10px] text-[#6B7280]">–</span>
        <Input
          type="time"
          value={value?.end || ""}
          onChange={(e) => onChange({ ...value, end: e.target.value })}
          data-testid={`${testidPrefix}-end`}
          className="h-8 text-xs w-[120px] min-w-0"
        />
      </div>
    </Field>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-[#4B5563] mb-1">{label}</p>
      {children}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Grade Schedule grid — one card per grade. The card opens a
// pattern picker (AM/PM/Full Day) and reveals a per-pattern editor
// (window + constraint blocks).
// ──────────────────────────────────────────────────────────────
function GradeScheduleGrid({ schoolId, school, gradeSchedules, visibleGrades, onSaved }) {
  const byGrade = useMemo(() => {
    const m = {};
    for (const g of gradeSchedules) m[g.grade] = g;
    return m;
  }, [gradeSchedules]);

  // Iter 33b — Two display modes:
  //   1. Compact grid (default): each grade card is a slim header with a
  //      Save chip + constraint count. Clicking the card (or Focus)
  //      goes straight to Focus mode — much clearer than inline expand.
  //   2. Focus mode: a single grade fills the full content width with a
  //      bigger, breathable layout. Prev/Next buttons step through the
  //      visible grades. Exit returns to the compact grid.
  const [focusGrade, setFocusGrade] = useState(null);

  if (!schoolId) {
    return <p className="text-xs text-[#6B7280] italic">Select a school first.</p>;
  }
  const grades = visibleGrades && visibleGrades.length > 0 ? visibleGrades : GRADE_OPTIONS;
  if (grades.length === 0) {
    return (
      <p
        data-testid="grade-grid-empty"
        className="text-xs text-[#6B7280] italic py-4 text-center"
      >
        All grades hidden. Click a grade chip above to show it.
      </p>
    );
  }

  if (focusGrade && grades.includes(focusGrade)) {
    const idx = grades.indexOf(focusGrade);
    const prevGrade = idx > 0 ? grades[idx - 1] : null;
    const nextGrade = idx < grades.length - 1 ? grades[idx + 1] : null;
    return (
      <div data-testid="grade-focus-mode" className="space-y-3">
        <div className="flex items-center justify-between">
          <button
            onClick={() => setFocusGrade(null)}
            data-testid="grade-focus-exit"
            className="text-[11px] font-semibold text-[#1E3F33] hover:text-[#173127] flex items-center gap-1"
          >
            ← Back to all grades
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={() => prevGrade && setFocusGrade(prevGrade)}
              disabled={!prevGrade}
              data-testid="grade-focus-prev"
              className="px-2 py-1 rounded-md text-[11px] font-semibold border border-[#d4d2cc] bg-white hover:bg-[#F3F1EC] disabled:opacity-30 disabled:cursor-not-allowed"
            >
              ← {prevGrade ? `Gr ${prevGrade}` : "—"}
            </button>
            <button
              onClick={() => nextGrade && setFocusGrade(nextGrade)}
              disabled={!nextGrade}
              data-testid="grade-focus-next"
              className="px-2 py-1 rounded-md text-[11px] font-semibold border border-[#d4d2cc] bg-white hover:bg-[#F3F1EC] disabled:opacity-30 disabled:cursor-not-allowed"
            >
              {nextGrade ? `Gr ${nextGrade}` : "—"} →
            </button>
          </div>
        </div>
        <GradeScheduleCard
          key={focusGrade}
          grade={focusGrade}
          schoolId={schoolId}
          school={school}
          existing={byGrade[focusGrade]}
          onSaved={onSaved}
          startCollapsed={false}
          focusMode
          onFocus={() => setFocusGrade(null)}
        />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
      {grades.map((grade) => (
        <GradeScheduleCard
          key={grade}
          grade={grade}
          schoolId={schoolId}
          school={school}
          existing={byGrade[grade]}
          onSaved={onSaved}
          startCollapsed
          onToggleCollapsed={() => setFocusGrade(grade)}
          onFocus={() => setFocusGrade(grade)}
        />
      ))}
    </div>
  );
}

function emptyPatterns(school) {
  // Iter 33d — Empty patterns INHERIT the school's main windows by
  // default. The customized_window flag stays false until the user
  // explicitly edits a time input, so any later change to the school's
  // window flows through transparently.
  const am = school?.attendance_window_am || PATTERN_DEFAULT_WINDOW.am;
  const pm = school?.attendance_window_pm || PATTERN_DEFAULT_WINDOW.pm;
  const fd = school?.attendance_window_full_day || PATTERN_DEFAULT_WINDOW.full_day;
  return {
    am: { window: { ...am }, customized_window: false, constraints: [] },
    pm: { window: { ...pm }, customized_window: false, constraints: [] },
    full_day: { window: { ...fd }, customized_window: false, constraints: [] },
  };
}

// Iter 33d — Compute the window that should be DISPLAYED for a pattern
// slot. When `customized_window` is true (user explicitly set times),
// render the stored window; otherwise live-proxy the school's main
// window so it tracks changes made on the School card.
function effectiveWindow(slot, pattern, school) {
  const customized = slot && slot.customized_window === true;
  if (customized && slot?.window) return slot.window;
  return (
    school?.[`attendance_window_${pattern}`] || slot?.window || PATTERN_DEFAULT_WINDOW[pattern]
  );
}

function GradeScheduleCard({
  grade,
  schoolId,
  school,
  existing,
  onSaved,
  startCollapsed = false,
  focusMode = false,
  onToggleCollapsed,
  onFocus,
}) {
  const [activePattern, setActivePattern] = useState("full_day");
  // Iter 43b — Multiple rows saved by the old fan-out behavior that
  // share (kind, label, start_time, end_time) but differ only in
  // day_of_week are now auto-merged into one multi-day row on load.
  // This both (a) stops the UI from re-displaying the old fanned-out
  // shape ("it still fans out" reports) and (b) gives the user a
  // single editable row they can add more days to freely. The fan-out
  // runs again at Save via expandPatternsForSave so the backend
  // storage shape is unchanged.
  const collapseDaysForEdit = (patterns) => {
    const out = {};
    for (const [key, slot] of Object.entries(patterns || {})) {
      const rows = slot?.constraints || [];
      const buckets = new Map();
      const order = [];
      for (const c of rows) {
        const dow = typeof c.day_of_week === "number" ? c.day_of_week : -1;
        const shapeKey = JSON.stringify({
          kind: c.kind,
          label: c.label || "",
          start_time: c.start_time || "",
          end_time: c.end_time || "",
        });
        if (dow === -1) {
          // Every-day rows stay as their own single-entry row.
          const uniq = `__every__${order.length}__${shapeKey}`;
          buckets.set(uniq, { ...c, days: [-1], day_of_week: -1 });
          order.push(uniq);
          continue;
        }
        if (buckets.has(shapeKey)) {
          const bucketRow = buckets.get(shapeKey);
          if (!bucketRow.days.includes(dow)) {
            bucketRow.days = [...bucketRow.days, dow].sort((a, b) => a - b);
          }
        } else {
          buckets.set(shapeKey, { ...c, days: [dow], day_of_week: dow });
          order.push(shapeKey);
        }
      }
      out[key] = { ...slot, constraints: order.map((k) => buckets.get(k)) };
    }
    return out;
  };

  const [draft, setDraft] = useState(() =>
    existing?.patterns ? collapseDaysForEdit(structuredClone(existing.patterns)) : emptyPatterns(school)
  );
  const [saving, setSaving] = useState(false);
  // Iter 33b — collapsed by default in the compact grid; always
  // expanded in focus mode.
  const collapsed = focusMode ? false : startCollapsed;

  // Iter 33 — Re-sync draft when the existing record changes OR when
  // the school's default windows change (only when there's no existing
  // patterns, so we don't nuke unsaved user edits on a real grade
  // schedule). Tracking the actual window strings avoids depending on
  // the school object identity which mutates each `load()`.
  const schoolWindowKey = JSON.stringify({
    am: school?.attendance_window_am,
    pm: school?.attendance_window_pm,
    full_day: school?.attendance_window_full_day,
  });
  useEffect(() => {
    if (existing?.patterns) setDraft(collapseDaysForEdit(structuredClone(existing.patterns)));
    else setDraft(emptyPatterns(school));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existing?.id, existing?.updated_at, schoolId, schoolWindowKey]);

  const dirty = JSON.stringify(draft) !== JSON.stringify(
    existing?.patterns ? collapseDaysForEdit(existing.patterns) : emptyPatterns(school)
  );

  const save = async () => {
    setSaving(true);
    try {
      // Iter 42 — Expand any multi-day `days[]` fields into per-day
      // single-day rows so the backend keeps its existing shape.
      const expandedPatterns = expandPatternsForSave(draft);
      const payload = { school_id: schoolId, grade, patterns: expandedPatterns };
      if (existing?.id) {
        await api.updateGradeSchedule(existing.id, payload);
      } else {
        await api.createGradeSchedule(payload);
      }
      // Reflect the saved (expanded) shape locally so the row list
      // visibly reconciles after save — matches what the server now
      // holds.
      setDraft(expandedPatterns);
      toast.success(`Grade ${grade} schedule saved`);
      onSaved();
    } catch (e) {
      toast.error(`Save failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const updatePattern = (key, partial) =>
    setDraft((d) => ({ ...d, [key]: { ...d[key], ...partial } }));

  // Iter 33d — Editing the time inputs marks the pattern as customized so
  // future changes to the school's main window won't silently overwrite
  // the user's explicit choice.
  const updateWindow = (key, win, opts = { markCustomized: true }) =>
    updatePattern(key, {
      window: win,
      ...(opts.markCustomized ? { customized_window: true } : {}),
    });

  // "Use school default" path — clears the customized flag so the slot
  // re-inherits live from school.attendance_window_<key>.
  const resetToSchool = (key) => {
    const w = school?.[`attendance_window_${key}`] || PATTERN_DEFAULT_WINDOW[key];
    updatePattern(key, { window: { ...w }, customized_window: false });
  };

  const addConstraint = (key) =>
    setDraft((d) => ({
      ...d,
      [key]: {
        ...d[key],
        constraints: [
          ...(d[key].constraints || []),
          {
            id: crypto.randomUUID(),
            label: "",
            kind: "lunch",
            day_of_week: -1,
            start_time: "11:00",
            end_time: "11:30",
          },
        ],
      },
    }));

  const updateConstraint = (key, idx, partial) =>
    setDraft((d) => ({
      ...d,
      [key]: {
        ...d[key],
        constraints: d[key].constraints.map((c, i) => (i === idx ? { ...c, ...partial } : c)),
      },
    }));

  const removeConstraint = (key, idx) =>
    setDraft((d) => ({
      ...d,
      [key]: {
        ...d[key],
        constraints: d[key].constraints.filter((_, i) => i !== idx),
      },
    }));

  // FP3-069 follow-up #11 — Copy a constraint row from one
  // attendance-pattern bucket to another. Skips destinations that
  // already contain a row with the same shape so users don't
  // silently dedupe-explode the bucket.
  //
  // Shape key matches the editor's collapseDaysForEdit dedup
  // signature: (kind, label, start_time, end_time, sorted day set).
  // We dedupe per-bucket so AM-saved lunch (Mon/Tue/Wed) doesn't
  // copy a duplicate into Full Day if Full Day already has the
  // identical row.
  //
  // Returns an object with `created` (count of buckets that received
  // a new copy) and `skipped` (count of buckets that already had it)
  // so the caller can surface honest toast feedback.
  const _shapeKey = (c) => {
    const days = Array.isArray(c.days) && c.days.length
      ? [...c.days].sort((a, b) => a - b)
      : [typeof c.day_of_week === "number" ? c.day_of_week : -1];
    return JSON.stringify({
      kind: (c.kind || "").trim().toLowerCase(),
      label: (c.label || "").trim().toLowerCase(),
      start_time: c.start_time || "",
      end_time: c.end_time || "",
      days,
    });
  };
  const copyConstraintTo = (sourceKey, idx, destKeys) => {
    const dests = destKeys.filter((k) => k !== sourceKey);
    if (dests.length === 0) {
      toast.info("Source and destination patterns are the same.");
      return;
    }
    let created = 0;
    let skipped = 0;
    setDraft((d) => {
      const sourceRow = d[sourceKey]?.constraints?.[idx];
      if (!sourceRow) return d;
      const sourceShape = _shapeKey(sourceRow);
      const out = { ...d };
      for (const dk of dests) {
        const slot = out[dk] || { window: PATTERN_DEFAULT_WINDOW[dk], customized_window: false, constraints: [] };
        const existing = slot.constraints || [];
        const dup = existing.some((c) => _shapeKey(c) === sourceShape);
        if (dup) {
          skipped += 1;
          continue;
        }
        // Clone with fresh id; preserve kind/label/days/times.
        const clone = {
          ...sourceRow,
          id: crypto.randomUUID(),
        };
        out[dk] = { ...slot, constraints: [...existing, clone] };
        created += 1;
      }
      return out;
    });
    if (created > 0 && skipped > 0) {
      toast.success(
        `Copied to ${created} pattern${created === 1 ? "" : "s"} · ${skipped} already had this rule`
      );
    } else if (created > 0) {
      toast.success(`Copied to ${created} pattern${created === 1 ? "" : "s"}`);
    } else if (skipped > 0) {
      toast.info(
        `Already exists in ${skipped} pattern${skipped === 1 ? "" : "s"} — nothing copied`
      );
    }
  };

  // Iter 42 — Expand multi-day constraints into per-day rows AT SAVE
  // TIME so the Grade Schedule storage keeps its single-day invariant
  // but the editor can treat multi-day picks as a first-class concept.
  // Runs over the three patterns (am/pm/full_day) and returns a new
  // `patterns` object safe to POST.
  const expandPatternsForSave = (patterns) => {
    const out = {};
    for (const [key, slot] of Object.entries(patterns || {})) {
      const rows = slot?.constraints || [];
      const expanded = [];
      for (const c of rows) {
        const days = Array.isArray(c.days) && c.days.length
          ? c.days
          : [typeof c.day_of_week === "number" ? c.day_of_week : -1];
        if (days.includes(-1)) {
          // Every-day collapses to one row
          const { days: _d, ...rest } = c;
          void _d;
          expanded.push({ ...rest, day_of_week: -1 });
          continue;
        }
        if (days.length <= 1) {
          const { days: _d, ...rest } = c;
          void _d;
          expanded.push({ ...rest, day_of_week: days[0] ?? -1 });
          continue;
        }
        for (const d of days) {
          const { days: _d, id: _id, ...rest } = c;
          void _d; void _id;
          expanded.push({
            ...rest,
            id: crypto.randomUUID(),
            day_of_week: d,
          });
        }
      }
      out[key] = { ...slot, constraints: expanded };
    }
    return out;
  };

  const slot = draft[activePattern] || { window: PATTERN_DEFAULT_WINDOW[activePattern], customized_window: false, constraints: [] };
  const displayWindow = effectiveWindow(slot, activePattern, school);
  const isCustomized = slot.customized_window === true;
  const totalConstraints =
    (draft.am?.constraints?.length || 0) +
    (draft.pm?.constraints?.length || 0) +
    (draft.full_day?.constraints?.length || 0);

  return (
    <div
      data-testid={`grade-card-${grade}`}
      data-grade={grade}
      data-mode={focusMode ? "focus" : collapsed ? "collapsed" : "expanded"}
      className={`border border-[#E5E3DB] rounded-xl bg-[#FAFAF7] overflow-hidden flex flex-col ${
        focusMode ? "shadow-md" : ""
      }`}
    >
      <div className="flex items-center justify-between px-3 py-2 border-b border-[#ECEAE4] bg-white gap-2">
        <button
          type="button"
          onClick={() => {
            if (focusMode) return;
            onToggleCollapsed && onToggleCollapsed();
          }}
          data-testid={`grade-card-${grade}-toggle`}
          className="flex items-baseline gap-2 flex-1 text-left hover:opacity-80 transition-opacity"
          disabled={focusMode}
        >
          {!focusMode && (
            <span className="text-[#6B7280] text-[10px] mr-0.5">{collapsed ? "▸" : "▾"}</span>
          )}
          <span className="text-base font-semibold text-[#1E3F33] heading-font">
            Grade {grade}
          </span>
          <span
            className="text-[10px] text-[#6B7280]"
            title={
              existing
                ? `Total across AM (${draft.am?.constraints?.length || 0}), PM (${
                    draft.pm?.constraints?.length || 0
                  }), Full Day (${
                    draft.full_day?.constraints?.length || 0
                  }). The Wizard only enforces constraints saved under the SAME attendance pattern as each student (e.g., a Full Day student only sees Full Day rules).`
                : ""
            }
          >
            {existing
              ? `${totalConstraints} constraints saved (AM + PM + Full Day)`
              : "not configured"}
          </span>
        </button>
        <div className="flex items-center gap-1">
          {!focusMode && onFocus && (
            <button
              onClick={onFocus}
              data-testid={`grade-card-${grade}-focus`}
              title="Edit only this grade in a larger view"
              className="text-[10px] font-semibold text-[#4A6B8C] hover:text-[#1E3F33] px-1.5 py-1 rounded-md hover:bg-[#E8F0EB]"
            >
              Focus
            </button>
          )}
          <Button
            size="sm"
            onClick={save}
            disabled={!dirty || saving}
            data-testid={`grade-card-save-${grade}`}
            className="bg-[#1E3F33] hover:bg-[#173127] text-white rounded-md h-7 text-[11px] px-2 disabled:opacity-40"
          >
            <FloppyDisk size={11} weight="bold" className="mr-1" />
            {saving ? "…" : dirty ? "Save" : "Saved"}
          </Button>
        </div>
      </div>
      {!collapsed && (
        <>
          <div className="flex border-b border-[#ECEAE4] bg-[#F3F1EC]">
            {PATTERN_KEYS.map((p) => {
              const active = activePattern === p;
              return (
                <button
                  key={p}
                  data-testid={`grade-card-${grade}-pattern-${p}`}
                  onClick={() => setActivePattern(p)}
                  className={`flex-1 ${focusMode ? "py-3 text-sm" : "py-2 text-[11px]"} font-semibold transition-colors ${
                    active
                      ? "bg-white text-[#1E3F33] border-b-2 border-[#1E3F33]"
                      : "text-[#6B7280] hover:text-[#1F2937]"
                  }`}
                >
                  {PATTERN_LABEL[p]}
                </button>
              );
            })}
          </div>
          <div className={`${focusMode ? "p-6 space-y-6" : "p-3 space-y-3"}`}>
            <div>
              <div className={`flex items-center justify-between ${focusMode ? "mb-3" : "mb-1"}`}>
                <p className={`${focusMode ? "text-xs" : "text-[10px]"} font-semibold uppercase tracking-wider text-[#4B5563] flex items-center gap-1.5`}>
                  School-day window ({PATTERN_LABEL[activePattern]})
                  {/* FP3-057 — Clearer pills. "inherits school" used to
                      read as "locked to school window" to some users
                      when in fact the inputs were always editable. New
                      copy: "Using school default" until edited, then
                      "Custom grade window" after the first override. */}
                  {!isCustomized && (
                    <span
                      data-testid={`grade-card-${grade}-${activePattern}-inherited-flag`}
                      className="text-[9px] font-normal text-[#4A6B8C] bg-[#EDF2F7] rounded px-1 py-0.5 normal-case tracking-normal"
                      title="These times start from the school full-day window. Edit them here to set a Grade-specific override."
                    >
                      Using school default
                    </span>
                  )}
                  {isCustomized && (
                    <span
                      data-testid={`grade-card-${grade}-${activePattern}-customized-flag`}
                      className="text-[9px] font-semibold text-[#1E3F33] bg-[#E8F0EB] rounded px-1 py-0.5 normal-case tracking-normal"
                      title="This grade's window is overriding the school default. Use Reset to school default to fall back."
                    >
                      Custom grade window
                    </span>
                  )}
                </p>
                {/* FP3-057 — Reset action; wording matches the user-
                    facing spec. Only shown after an override exists so
                    we don't add visual noise to non-customized rows. */}
                {school && isCustomized && (
                  <button
                    type="button"
                    onClick={() => resetToSchool(activePattern)}
                    data-testid={`grade-card-${grade}-${activePattern}-use-school`}
                    className={`${focusMode ? "text-xs" : "text-[10px]"} font-semibold text-[#4A6B8C] hover:text-[#1E3F33]`}
                    title="Reset to the school's main window for this pattern"
                  >
                    Reset to school default
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Input
                  type="time"
                  value={displayWindow?.start || ""}
                  onChange={(e) =>
                    updateWindow(activePattern, { ...displayWindow, start: e.target.value })
                  }
                  data-testid={`grade-card-${grade}-${activePattern}-window-start`}
                  className={focusMode ? "h-10 text-sm w-[160px]" : "h-7 text-xs w-[120px]"}
                />
                <span className="text-xs text-[#6B7280]">–</span>
                <Input
                  type="time"
                  value={displayWindow?.end || ""}
                  onChange={(e) =>
                    updateWindow(activePattern, { ...displayWindow, end: e.target.value })
                  }
                  data-testid={`grade-card-${grade}-${activePattern}-window-end`}
                  className={focusMode ? "h-10 text-sm w-[160px]" : "h-7 text-xs w-[120px]"}
                />
              </div>
              {/* FP3-057 — Inline help so users don't second-guess
                  whether the times are locked. */}
              {!isCustomized && (
                <p
                  data-testid={`grade-card-${grade}-${activePattern}-inherit-help`}
                  className={`${focusMode ? "text-[11px] mt-2" : "text-[10px] mt-1"} text-[#6B7280] italic`}
                >
                  Starts from the school full-day default — edit either
                  time to set a Grade {grade} override.
                </p>
              )}
            </div>
            <div>
              <div className={`flex items-center justify-between ${focusMode ? "mb-3" : "mb-1.5"}`}>
                <p className={`${focusMode ? "text-xs" : "text-[10px]"} font-semibold uppercase tracking-wider text-[#4B5563]`}>
                  Constraint blocks ({slot.constraints?.length || 0})
                </p>
                <button
                  onClick={() => addConstraint(activePattern)}
                  data-testid={`grade-card-${grade}-${activePattern}-add`}
                  className={`${focusMode ? "text-xs" : "text-[10px]"} font-semibold text-[#1E3F33] hover:text-[#173127] flex items-center gap-1`}
                >
                  <Plus size={focusMode ? 12 : 10} /> Add
                </button>
              </div>
              {(slot.constraints || []).length === 0 ? (
                <p className={`${focusMode ? "text-sm" : "text-[11px]"} text-[#6B7280] italic py-1`}>
                  No {PATTERN_LABEL[activePattern]} constraints. Add lunch / recess / specials as needed.
                </p>
              ) : (
                <div className={focusMode ? "space-y-3" : "space-y-1.5"}>
                  {slot.constraints.map((c, idx) => (
                    <ConstraintRow
                      key={c.id || idx}
                      c={c}
                      focusMode={focusMode}
                      testidPrefix={`grade-card-${grade}-${activePattern}-c${idx}`}
                      onChange={(partial) => updateConstraint(activePattern, idx, partial)}
                      onRemove={() => removeConstraint(activePattern, idx)}
                      sourcePattern={activePattern}
                      onCopyTo={(dests) => copyConstraintTo(activePattern, idx, dests)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function ConstraintRow({ c, testidPrefix, focusMode = false, onChange, onRemove, sourcePattern, onCopyTo }) {
  // Iter 42 — Multi-day selection lives on `c.days` (int[]) WITHOUT
  // fanning out during picking. The single-day `day_of_week` is kept
  // in sync with the first entry so legacy readers keep working; the
  // save path expands `days`→ N rows once the user clicks Save.
  const inputH = focusMode ? "h-9 text-xs" : "h-7 text-[11px]";
  const timeW = focusMode ? "w-[140px]" : "w-[110px]";
  const currentDays = Array.isArray(c.days) && c.days.length
    ? c.days
    : [typeof c.day_of_week === "number" ? c.day_of_week : -1];
  const handleDaysChange = (next) => {
    if (!Array.isArray(next) || next.length === 0) return;
    // Mirror the first entry into day_of_week so legacy back-compat
    // (including older rows that only carry day_of_week) stays stable.
    onChange({ days: next, day_of_week: next[0] });
  };
  // FP3-069 follow-up #11 — Convenience "Copy to..." menu so users
  // can mirror an AM-saved lunch into PM / Full Day without manually
  // re-creating it. Dedupe lives in the parent's `copyConstraintTo`
  // helper.
  const [copyOpen, setCopyOpen] = useState(false);
  const COPY_TARGETS = PATTERN_KEYS.filter((p) => p !== sourcePattern);
  return (
    <div
      className="flex flex-col gap-1.5 p-2 rounded-md bg-white border border-[#ECEAE4]"
      data-testid={testidPrefix}
    >
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={c.kind}
          onChange={(e) => onChange({ kind: e.target.value })}
          data-testid={`${testidPrefix}-kind`}
          className={`${inputH} border border-[#d4d2cc] rounded-md px-1 bg-white shrink-0 w-[90px]`}
        >
          {nativeOptions(KIND_OPTIONS, (k) => ({ value: k, label: k }))}
        </select>
        <Input
          value={c.label}
          onChange={(e) => onChange({ label: e.target.value })}
          placeholder="Label (e.g. Gr 2 lunch)"
          data-testid={`${testidPrefix}-label`}
          className={`${inputH} flex-1 min-w-[140px]`}
        />
        <Input
          type="time"
          value={c.start_time}
          onChange={(e) => onChange({ start_time: e.target.value })}
          data-testid={`${testidPrefix}-start`}
          className={`${inputH} ${timeW}`}
        />
        <Input
          type="time"
          value={c.end_time}
          onChange={(e) => onChange({ end_time: e.target.value })}
          data-testid={`${testidPrefix}-end`}
          className={`${inputH} ${timeW}`}
        />
        {onCopyTo && sourcePattern && (
          <div className="relative">
            <button
              type="button"
              onClick={() => setCopyOpen((o) => !o)}
              data-testid={`${testidPrefix}-copy`}
              title="Copy this rule to another attendance pattern"
              className="text-[10px] font-semibold text-[#4A6B8C] hover:text-[#1E3F33] bg-[#EEF2F7] hover:bg-[#DDE6F0] rounded px-1.5 py-1"
            >
              Copy to…
            </button>
            {copyOpen && (
              <>
                {/* Click-outside backdrop. Stays transparent so it
                    never shadows the editor. */}
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setCopyOpen(false)}
                  data-testid={`${testidPrefix}-copy-backdrop`}
                />
                <div
                  data-testid={`${testidPrefix}-copy-menu`}
                  className="absolute right-0 top-full mt-1 z-20 min-w-[150px] bg-white border border-[#E5E3DB] rounded-lg shadow-lg p-1"
                >
                  {COPY_TARGETS.map((p) => (
                    <button
                      key={p}
                      type="button"
                      data-testid={`${testidPrefix}-copy-to-${p}`}
                      onClick={() => {
                        onCopyTo([p]);
                        setCopyOpen(false);
                      }}
                      className="w-full text-left text-[11px] px-2 py-1.5 rounded hover:bg-[#F1F2F4] text-[#1F2937]"
                    >
                      Copy to {PATTERN_LABEL[p]}
                    </button>
                  ))}
                  {COPY_TARGETS.length > 1 && (
                    <>
                      <div className="my-1 border-t border-[#F1F2F4]" />
                      <button
                        type="button"
                        data-testid={`${testidPrefix}-copy-to-all`}
                        onClick={() => {
                          onCopyTo(COPY_TARGETS);
                          setCopyOpen(false);
                        }}
                        className="w-full text-left text-[11px] px-2 py-1.5 rounded hover:bg-[#E8F0EB] text-[#1E3F33] font-semibold"
                      >
                        Copy to all patterns
                      </button>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        )}
        <button
          onClick={onRemove}
          data-testid={`${testidPrefix}-remove`}
          className="text-[#6B7280] hover:text-[#B85C5C] p-1"
        >
          <Trash size={focusMode ? 14 : 11} />
        </button>
      </div>
      <DayOfWeekMultiPicker
        testid={`${testidPrefix}-days`}
        value={currentDays}
        onChange={handleDaysChange}
        dayBase={0}
        showEveryDay
      />
      {currentDays.length > 1 && currentDays[0] !== -1 && (
        <p className="text-[10px] text-[#6B7280] italic pl-0.5" data-testid={`${testidPrefix}-fanout-hint`}>
          Applies to {currentDays.length} days — will be saved as {currentDays.length} rows on Save.
        </p>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Flex Day rows — unchanged from prior version.
// ──────────────────────────────────────────────────────────────
function FlexDayRow({ f, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(f);
  // FP3-032D — In-app delete confirmation replaces window.confirm.
  const [pendingDelete, setPendingDelete] = useState(false);
  const [removing, setRemoving] = useState(false);
  useEffect(() => setDraft(f), [f]);
  const save = async () => {
    try {
      await api.updateFlexDay(f.id, {
        school_id: draft.school_id,
        kind: draft.kind,
        date: draft.kind === "specific_date" ? draft.date || null : null,
        day_of_week: draft.kind === "recurring_weekday" ? parseInt(draft.day_of_week, 10) : null,
        altered_start: draft.altered_start,
        altered_end: draft.altered_end,
        label: draft.label || "",
        closed: !!draft.closed,
      });
      toast.success("Updated");
      setEditing(false);
      onSaved();
    } catch (e) {
      toast.error(`Save failed: ${e.message}`);
    }
  };
  const confirmRemove = async () => {
    setRemoving(true);
    try {
      await api.deleteFlexDay(f.id);
      toast.success("Deleted");
      setPendingDelete(false);
      onSaved();
    } catch (e) {
      toast.error(`Delete failed: ${e.message}`);
    } finally {
      setRemoving(false);
    }
  };
  if (!editing) {
    const whenLabel =
      f.kind === "specific_date"
        ? f.date || "—"
        : f.day_of_week !== null && f.day_of_week !== undefined
        ? `Every ${DAY_LABELS[f.day_of_week]}`
        : "—";
    const windowLabel = f.closed ? "School closed" : `${f.altered_start}–${f.altered_end}`;
    return (
      <div
        data-testid={`flex-row-${f.id}`}
        className="grid grid-cols-[100px_120px_180px_1fr_70px_70px] gap-2 px-3 py-2 items-center text-[11px]"
      >
        <span className="text-[10px] uppercase tracking-wider text-[#5C7088]">
          {f.kind === "specific_date" ? "Specific" : "Recurring"}
        </span>
        <span className="text-[#1F2937]">{whenLabel}</span>
        <span
          className={`text-[10px] tabular-nums ${
            f.closed ? "text-[#B85C5C] font-semibold" : "text-[#4B5563]"
          }`}
        >
          {windowLabel}
        </span>
        <span className="text-[#1F2937] truncate">{f.label || "—"}</span>
        <span className="text-[10px] text-[#6B7280]">{f.closed ? "Yes" : "No"}</span>
        <div className="flex justify-end gap-1">
          <button
            onClick={() => setEditing(true)}
            className="text-[#6B7280] hover:text-[#1E3F33] p-1"
            data-testid={`flex-edit-${f.id}`}
          >
            <Pencil size={11} />
          </button>
          <button
            onClick={() => setPendingDelete(true)}
            className="text-[#6B7280] hover:text-[#B85C5C] p-1"
            data-testid={`flex-delete-${f.id}`}
            aria-label="Delete flex day"
          >
            <Trash size={11} />
          </button>
        </div>
        <ConfirmDialog
          open={pendingDelete}
          onOpenChange={(o) => !removing && setPendingDelete(o)}
          title="Delete this flex day?"
          description="The schedule window for this date will revert to the school's default."
          confirmLabel="Delete flex day"
          destructive
          onConfirm={confirmRemove}
          testId={`flex-delete-confirm-${f.id}`}
        />
      </div>
    );
  }
  return (
    <div className="grid grid-cols-[100px_120px_180px_1fr_70px_70px] gap-2 px-3 py-2 items-center text-[11px]">
      <select
        value={draft.kind}
        onChange={(e) => setDraft({ ...draft, kind: e.target.value })}
        className="h-7 text-xs border border-[#d4d2cc] rounded-md px-1 bg-white"
      >
        <option value="specific_date">specific</option>
        <option value="recurring_weekday">recurring</option>
      </select>
      {draft.kind === "specific_date" ? (
        <Input
          type="date"
          value={draft.date || ""}
          onChange={(e) => setDraft({ ...draft, date: e.target.value })}
          className="h-7 text-xs"
        />
      ) : (
        <select
          value={draft.day_of_week ?? 2}
          onChange={(e) => setDraft({ ...draft, day_of_week: parseInt(e.target.value, 10) })}
          className="h-7 text-xs border border-[#d4d2cc] rounded-md px-1 bg-white"
        >
          {nativeOptions(DAY_LABELS, (d, i) => ({ value: i, key: d, label: `Every ${d}` }))}
        </select>
      )}
      <div className="flex items-center gap-1">
        <Input
          type="time"
          value={draft.altered_start}
          onChange={(e) => setDraft({ ...draft, altered_start: e.target.value })}
          className="h-7 text-xs w-20"
          disabled={draft.closed}
        />
        <Input
          type="time"
          value={draft.altered_end}
          onChange={(e) => setDraft({ ...draft, altered_end: e.target.value })}
          className="h-7 text-xs w-20"
          disabled={draft.closed}
        />
      </div>
      <Input
        value={draft.label || ""}
        onChange={(e) => setDraft({ ...draft, label: e.target.value })}
        placeholder="Label"
        className="h-7 text-xs"
      />
      <label className="flex items-center gap-1 text-[10px] cursor-pointer">
        <input
          type="checkbox"
          checked={!!draft.closed}
          onChange={(e) => setDraft({ ...draft, closed: e.target.checked })}
        />
        closed
      </label>
      <div className="flex justify-end gap-1">
        <button
          onClick={save}
          className="text-[#1E3F33] p-1"
          data-testid={`flex-save-${f.id}`}
        >
          <FloppyDisk size={12} weight="bold" />
        </button>
        <button onClick={() => setEditing(false)} className="text-[#6B7280] p-1">
          <X size={12} />
        </button>
      </div>
    </div>
  );
}

function AddFlexDayButton({ schoolId, onCreated }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({
    kind: "recurring_weekday",
    day_of_week: 2,
    date: "",
    altered_start: "08:00",
    altered_end: "12:00",
    label: "",
    closed: false,
  });
  const create = async () => {
    if (!schoolId) return toast.error("Select a school first");
    try {
      const base = {
        school_id: schoolId,
        kind: draft.kind,
        date: draft.kind === "specific_date" ? draft.date || null : null,
        altered_start: draft.altered_start,
        altered_end: draft.altered_end,
        label: draft.label,
        closed: draft.closed,
      };
      if (draft.kind === "recurring_weekday") {
        // Iter 40 — multi-day support: fan out N rows when several
        // weekdays are selected.
        const rows = expandDays(draft, "day_of_week").map((r) => ({
          ...base,
          day_of_week: r.day_of_week,
        }));
        await Promise.all(rows.map((r) => api.createFlexDay(r)));
        toast.success(rows.length > 1 ? `Added ${rows.length} days` : "Added");
      } else {
        await api.createFlexDay({ ...base, day_of_week: null });
        toast.success("Added");
      }
      setOpen(false);
      onCreated();
    } catch (e) {
      toast.error(`Create failed: ${e.message}`);
    }
  };
  if (!open) {
    return (
      <Button
        size="sm"
        variant="outline"
        onClick={() => setOpen(true)}
        data-testid="flex-add"
        className="rounded-lg h-7 text-[11px] border-[#d4d2cc]"
      >
        <Plus size={11} className="mr-1" />
        Add flex day
      </Button>
    );
  }
  return (
    <div className="flex items-center gap-1.5" data-testid="flex-add-form">
      <select
        value={draft.kind}
        onChange={(e) => setDraft({ ...draft, kind: e.target.value })}
        className="h-7 text-xs border border-[#d4d2cc] rounded-md px-1 bg-white"
      >
        <option value="specific_date">specific</option>
        <option value="recurring_weekday">recurring</option>
      </select>
      {draft.kind === "specific_date" ? (
        <Input
          type="date"
          value={draft.date}
          onChange={(e) => setDraft({ ...draft, date: e.target.value })}
          className="h-7 text-xs"
        />
      ) : (
        <DayOfWeekMultiPicker
          testid="flex-add-days"
          value={draft.days || [draft.day_of_week]}
          onChange={(next) => setDraft({ ...draft, days: next, day_of_week: next[0] })}
          showEveryDay={false}
        />
      )}
      <Input
        type="time"
        value={draft.altered_start}
        onChange={(e) => setDraft({ ...draft, altered_start: e.target.value })}
        className="h-7 text-xs w-20"
      />
      <Input
        type="time"
        value={draft.altered_end}
        onChange={(e) => setDraft({ ...draft, altered_end: e.target.value })}
        className="h-7 text-xs w-20"
      />
      <Input
        value={draft.label}
        onChange={(e) => setDraft({ ...draft, label: e.target.value })}
        placeholder="Label"
        className="h-7 text-xs w-28"
      />
      <label className="flex items-center gap-1 text-[10px] cursor-pointer">
        <input
          type="checkbox"
          checked={draft.closed}
          onChange={(e) => setDraft({ ...draft, closed: e.target.checked })}
        />
        closed
      </label>
      <Button
        size="sm"
        onClick={create}
        className="bg-[#1E3F33] text-white rounded-lg h-7 text-[11px] px-2"
        data-testid="flex-add-save"
      >
        Save
      </Button>
      <button onClick={() => setOpen(false)} className="text-[#6B7280] p-1">
        <X size={11} />
      </button>
    </div>
  );
}


// ──────────────────────────────────────────────────────────────
// Teacher-Dependent Constraints — Iter 33
// Use when a constraint (e.g. recess timing) varies by classroom
// teacher rather than by grade. Each row = one school × teacher × kind
// × day × time-window. Wizard layers these on top of grade rules.
// ──────────────────────────────────────────────────────────────
function TeacherConstraintsCard({ schoolId, constraints, onSaved }) {
  const [adding, setAdding] = useState(false);
  const [addingTeacher, setAddingTeacher] = useState(false);
  // Iter 33b — fetch the teacher directory so the editor's dropdown is
  // driven by canonical teacher_emails on student records (not free
  // text). Empty state nudges the provider to add teacher emails on
  // student pages first.
  const [teachers, setTeachers] = useState([]);
  const [loadingTeachers, setLoadingTeachers] = useState(true);
  const loadTeachers = async () => {
    setLoadingTeachers(true);
    try {
      const r = await api.listTeachers();
      setTeachers(r.data || []);
    } catch {
      setTeachers([]);
    } finally {
      setLoadingTeachers(false);
    }
  };
  useEffect(() => {
    loadTeachers();
  }, []);
  const teacherByEmail = useMemo(() => {
    const m = {};
    for (const t of teachers) m[t.email] = t;
    return m;
  }, [teachers]);

  if (!schoolId) {
    return <p className="text-xs text-[#6B7280] italic">Select a school first.</p>;
  }
  return (
    <div className="space-y-2" data-testid="teacher-constraints-card">
      {teachers.length === 0 && !loadingTeachers && !addingTeacher && (
        <p
          data-testid="teacher-constraints-empty-directory"
          className="text-[11px] text-[#D9A05B] bg-[#FDF8F3] border border-[#F0DFC4] rounded-md px-2 py-1.5"
        >
          No teacher emails on file. Add one below or from any Student → Service Demographics page.
        </p>
      )}
      {constraints.length === 0 && !adding ? (
        <p className="text-[11px] text-[#6B7280] italic">
          No teacher-dependent constraints. Use the button below to add one
          (e.g. <em>Mrs. Patel · Grade K · recess · Mon · 10:15–10:30</em>).
        </p>
      ) : (
        <div className="border border-[#E5E3DB] rounded-lg overflow-hidden">
          <div className="grid grid-cols-[1fr_70px_80px_70px_140px_60px] gap-2 px-3 py-2 bg-[#F3F1EC] text-[10px] font-semibold uppercase tracking-wider text-[#4B5563]">
            <span>Teacher / Label</span>
            <span>Grade</span>
            <span>Kind</span>
            <span>Day</span>
            <span>Time</span>
            <span></span>
          </div>
          <div className="divide-y divide-[#ECEAE4]">
            {constraints.map((tc) => (
              <TeacherConstraintRow
                key={tc.id}
                tc={tc}
                teacherByEmail={teacherByEmail}
                teachers={teachers}
                onSaved={onSaved}
                onAddTeacher={() => setAddingTeacher(true)}
              />
            ))}
          </div>
        </div>
      )}
      {addingTeacher && (
        <AddTeacherInline
          onCancel={() => setAddingTeacher(false)}
          onSaved={() => {
            setAddingTeacher(false);
            loadTeachers();
          }}
        />
      )}
      {adding ? (
        <TeacherConstraintEditor
          schoolId={schoolId}
          teachers={teachers}
          onCancel={() => setAdding(false)}
          onSaved={() => {
            setAdding(false);
            onSaved();
            loadTeachers();
          }}
          onAddTeacher={() => setAddingTeacher(true)}
        />
      ) : (
        <div className="flex items-center gap-2 pt-1">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setAdding(true)}
            disabled={teachers.length === 0}
            data-testid="teacher-constraint-add"
            className="rounded-lg h-7 text-[11px] border-[#d4d2cc] disabled:opacity-50"
          >
            <Plus size={11} className="mr-1" />
            Add teacher constraint
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setAddingTeacher(true)}
            data-testid="teacher-add-standalone"
            className="rounded-lg h-7 text-[11px] border-[#d4d2cc]"
          >
            <Plus size={11} className="mr-1" />
            Add teacher
          </Button>
        </div>
      )}
    </div>
  );
}

function AddTeacherInline({ onCancel, onSaved }) {
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    const trimmed = email.trim();
    if (!trimmed || !trimmed.includes("@")) {
      toast.error("Enter a valid email");
      return;
    }
    setSaving(true);
    try {
      await api.createTeacher({ email: trimmed });
      toast.success("Teacher added");
      onSaved();
    } catch (e) {
      toast.error(`Save failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
    }
  };
  return (
    <div
      className="border border-[#E5E3DB] rounded-lg p-3 bg-[#F3F8F4] flex items-center gap-2"
      data-testid="teacher-add-inline"
    >
      <Input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && save()}
        placeholder="Teacher email (e.g. t.jones@lincoln.k12.us)"
        data-testid="teacher-add-email"
        className="h-8 text-xs flex-1"
        autoFocus
      />
      <Button
        size="sm"
        onClick={save}
        disabled={saving || !email.trim()}
        data-testid="teacher-add-save"
        className="bg-[#1E3F33] hover:bg-[#173127] text-white rounded-md h-8 text-[11px] px-3 disabled:opacity-40"
      >
        <FloppyDisk size={11} weight="bold" className="mr-1" />
        {saving ? "…" : "Add"}
      </Button>
      <Button
        size="sm"
        variant="outline"
        onClick={onCancel}
        data-testid="teacher-add-cancel"
        className="rounded-md h-8 text-[11px] px-3 border-[#d4d2cc]"
      >
        Cancel
      </Button>
    </div>
  );
}

function TeacherConstraintRow({ tc, teacherByEmail, teachers, onSaved, onAddTeacher }) {
  const [editing, setEditing] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const display =
    tc.teacher_display ||
    teacherByEmail?.[tc.teacher]?.display_name ||
    tc.teacher ||
    "—";
  const remove = async () => {
    setDeleting(true);
    try {
      await api.deleteTeacherConstraint(tc.id);
      toast.success("Deleted");
      setConfirmingDelete(false);
      onSaved();
    } catch (e) {
      toast.error(`Delete failed: ${e.message}`);
    } finally {
      setDeleting(false);
    }
  };
  if (editing) {
    return (
      <div className="px-3 py-2 bg-[#FAFAF7]">
        <TeacherConstraintEditor
          schoolId={tc.school_id}
          existing={tc}
          teachers={teachers}
          onCancel={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            onSaved();
          }}
          onAddTeacher={onAddTeacher}
        />
      </div>
    );
  }
  const dayLabel =
    tc.day_of_week === -1 || tc.day_of_week === null || tc.day_of_week === undefined
      ? "every"
      : DAY_LABELS[tc.day_of_week] || "—";
  return (
    <>
      <div
        data-testid={`teacher-constraint-row-${tc.id}`}
        className="grid grid-cols-[1fr_70px_80px_70px_140px_60px] gap-2 px-3 py-2 items-center text-[11px]"
      >
        <span className="text-[#1F2937] truncate">
          <span className="font-semibold">{display}</span>
          {tc.teacher && tc.teacher.includes("@") && (
            <span className="text-[#9B9890]"> · {tc.teacher}</span>
          )}
          {tc.label && <span className="text-[#6B7280]"> · {tc.label}</span>}
        </span>
        <span className="text-[#4B5563]">{tc.grade || "—"}</span>
        <span className="text-[#4B5563] uppercase tracking-wider text-[10px]">{tc.kind}</span>
        <span className="text-[#4B5563]">{dayLabel}</span>
        <span className="text-[#4B5563] tabular-nums">
          {tc.start_time}–{tc.end_time}
        </span>
        <div className="flex justify-end gap-1">
          <button
            onClick={() => setEditing(true)}
            data-testid={`teacher-constraint-edit-${tc.id}`}
            className="text-[#6B7280] hover:text-[#1E3F33] p-1"
          >
            <Pencil size={11} />
          </button>
          <button
            onClick={() => setConfirmingDelete(true)}
            data-testid={`teacher-constraint-delete-${tc.id}`}
            className="text-[#6B7280] hover:text-[#B85C5C] p-1"
          >
            <Trash size={11} />
          </button>
        </div>
      </div>
      <AlertDialog open={confirmingDelete} onOpenChange={setConfirmingDelete}>
        <AlertDialogContent data-testid="teacher-constraint-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete teacher constraint?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the {tc.kind} block for{" "}
              <span className="font-semibold">{display}</span> at {tc.start_time}–{tc.end_time}.
              Cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="teacher-constraint-delete-cancel"
              disabled={deleting}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={remove}
              disabled={deleting}
              data-testid="teacher-constraint-delete-confirm"
              className="bg-[#B85C5C] hover:bg-[#9b4747] text-white"
            >
              {deleting ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function TeacherConstraintEditor({ schoolId, existing, teachers, onCancel, onSaved, onAddTeacher }) {
  const [draft, setDraft] = useState(
    existing
      ? { ...existing }
      : {
          school_id: schoolId,
          teacher: teachers?.[0]?.email || "",
          grade: "0",
          kind: "recess",
          day_of_week: -1,
          start_time: "10:15",
          end_time: "10:30",
          label: "",
        }
  );
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!draft.teacher.trim()) {
      toast.error("Teacher required");
      return;
    }
    setSaving(true);
    try {
      // Iter 40 — multi-day support: when creating, fan out one row
      // per selected day. When editing an existing constraint, keep
      // single-day semantics so the edit stays in place.
      const basePayload = {
        school_id: schoolId,
        teacher: draft.teacher.trim(),
        grade: draft.grade,
        kind: draft.kind,
        start_time: draft.start_time,
        end_time: draft.end_time,
        label: draft.label || "",
      };
      if (existing?.id) {
        await api.updateTeacherConstraint(existing.id, {
          ...basePayload,
          day_of_week: parseInt(draft.day_of_week, 10),
        });
        toast.success("Updated");
      } else {
        const rows = expandDays(draft, "day_of_week").map((r) => ({
          ...basePayload,
          day_of_week: r.day_of_week,
        }));
        await Promise.all(rows.map((r) => api.createTeacherConstraint(r)));
        toast.success(rows.length > 1 ? `Added ${rows.length} days` : "Added");
      }
      onSaved();
    } catch (e) {
      toast.error(`Save failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
    }
  };
  return (
    <div
      data-testid="teacher-constraint-editor"
      className="border border-[#E5E3DB] rounded-lg p-3 bg-[#FAFAF7] space-y-2"
    >
      <div className="grid grid-cols-1 md:grid-cols-[1.4fr_80px_110px_80px] gap-2 items-center">
        <select
          value={draft.teacher}
          onChange={(e) => {
            if (e.target.value === "__add_new__") {
              onAddTeacher && onAddTeacher();
              return;
            }
            setDraft({ ...draft, teacher: e.target.value });
          }}
          data-testid="teacher-constraint-teacher"
          className="h-8 text-xs border border-[#d4d2cc] rounded-md px-2 bg-white"
        >
          {teachers.length === 0
            ? nativeOptions([{ value: "", label: "— no teachers on file —" }], (o) => o)
            : nativeOptions(teachers, (t) => ({
                value: t.email,
                key: t.email,
                label: `${t.display_name} (${t.student_count})`,
              }))}
          <option value="__add_new__">+ Add new teacher…</option>
        </select>
        <select
          value={draft.grade}
          onChange={(e) => setDraft({ ...draft, grade: e.target.value })}
          data-testid="teacher-constraint-grade"
          className="h-8 text-xs border border-[#d4d2cc] rounded-md px-2 bg-white"
        >
          {nativeOptions(GRADE_OPTIONS, (g) => ({ value: g, label: `Gr ${g}` }))}
        </select>
        <select
          value={draft.kind}
          onChange={(e) => setDraft({ ...draft, kind: e.target.value })}
          data-testid="teacher-constraint-kind"
          className="h-8 text-xs border border-[#d4d2cc] rounded-md px-2 bg-white"
        >
          {nativeOptions(KIND_OPTIONS, (k) => ({ value: k, label: k }))}
        </select>
        {existing?.id ? (
          <select
            value={draft.day_of_week}
            onChange={(e) => setDraft({ ...draft, day_of_week: parseInt(e.target.value, 10) })}
            data-testid="teacher-constraint-day"
            className="h-8 text-xs border border-[#d4d2cc] rounded-md px-2 bg-white"
          >
            <option value={-1}>every</option>
            {nativeOptions(DAY_LABELS, (d, i) => ({ value: i, key: d, label: d }))}
          </select>
        ) : null}
      </div>
      {!existing?.id && (
        <div className="flex items-center gap-2 px-1">
          <span className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold shrink-0">Days</span>
          <DayOfWeekMultiPicker
            testid="teacher-constraint-days"
            value={draft.days || [draft.day_of_week === -1 ? -1 : 1]}
            onChange={(next) => setDraft({ ...draft, days: next, day_of_week: next[0] })}
          />
        </div>
      )}
      <div className="hidden">{/* spacer for prior layout sibling */}</div>
      <div className="grid grid-cols-1 md:grid-cols-[260px_1fr_auto] gap-2 items-center">
        <div className="flex items-center gap-1">
          <Input
            type="time"
            value={draft.start_time}
            onChange={(e) => setDraft({ ...draft, start_time: e.target.value })}
            data-testid="teacher-constraint-start"
            className="h-8 text-xs w-[120px]"
          />
          <span className="text-[10px] text-[#6B7280]">–</span>
          <Input
            type="time"
            value={draft.end_time}
            onChange={(e) => setDraft({ ...draft, end_time: e.target.value })}
            data-testid="teacher-constraint-end"
            className="h-8 text-xs w-[120px]"
          />
        </div>
        <Input
          value={draft.label}
          onChange={(e) => setDraft({ ...draft, label: e.target.value })}
          placeholder="Optional label / note"
          data-testid="teacher-constraint-label"
          className="h-8 text-xs"
        />
        <div className="flex items-center gap-1 justify-end">
          <Button
            size="sm"
            onClick={save}
            disabled={saving || !draft.teacher}
            data-testid="teacher-constraint-save"
            className="bg-[#1E3F33] hover:bg-[#173127] text-white rounded-md h-8 text-[11px] px-3 disabled:opacity-40"
          >
            <FloppyDisk size={11} weight="bold" className="mr-1" />
            {saving ? "…" : "Save"}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={onCancel}
            data-testid="teacher-constraint-cancel"
            className="rounded-md h-8 text-[11px] px-3 border-[#d4d2cc]"
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Calendar / Session Exceptions — Iter 33b
// Date-range blackouts (holidays, breaks, closures). Wired into
// _materialize_sessions on the backend so generated sessions skip
// these dates automatically.
// ──────────────────────────────────────────────────────────────
const SE_KIND_OPTIONS = [
  { value: "holiday", label: "Holiday" },
  { value: "break", label: "Break" },
  { value: "closure", label: "Closure" },
];

function SessionExceptionsCard({ schoolId, schools, exceptions, onSaved }) {
  const [adding, setAdding] = useState(false);
  return (
    <div className="space-y-2" data-testid="session-exceptions-card">
      {exceptions.length === 0 ? (
        <p className="text-[11px] text-[#6B7280] italic">
          No calendar exceptions yet. Add school breaks (Spring Break, Winter Break),
          holidays, or one-off closures so sessions skip those dates automatically.
        </p>
      ) : (
        <div className="border border-[#E5E3DB] rounded-lg overflow-hidden">
          <div className="grid grid-cols-[100px_220px_1fr_120px_60px] gap-2 px-3 py-2 bg-[#F3F1EC] text-[10px] font-semibold uppercase tracking-wider text-[#4B5563]">
            <span>Kind</span>
            <span>Dates</span>
            <span>Label</span>
            <span>Scope</span>
            <span></span>
          </div>
          <div className="divide-y divide-[#ECEAE4]">
            {exceptions.map((ex) => (
              <SessionExceptionRow key={ex.id} ex={ex} schools={schools} onSaved={onSaved} />
            ))}
          </div>
        </div>
      )}
      {adding ? (
        <SessionExceptionEditor
          schools={schools}
          defaultSchoolId={schoolId}
          onCancel={() => setAdding(false)}
          onSaved={() => {
            setAdding(false);
            onSaved();
          }}
        />
      ) : (
        <Button
          size="sm"
          variant="outline"
          onClick={() => setAdding(true)}
          data-testid="session-exception-add"
          className="rounded-lg h-7 text-[11px] border-[#d4d2cc]"
        >
          <Plus size={11} className="mr-1" />
          Add calendar exception
        </Button>
      )}
    </div>
  );
}

function SessionExceptionRow({ ex, schools, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const remove = async () => {
    setDeleting(true);
    try {
      await api.deleteSessionException(ex.id);
      toast.success("Deleted");
      setConfirmingDelete(false);
      onSaved();
    } catch (e) {
      toast.error(`Delete failed: ${e.message}`);
    } finally {
      setDeleting(false);
    }
  };
  if (editing) {
    return (
      <div className="px-3 py-2 bg-[#FAFAF7]">
        <SessionExceptionEditor
          existing={ex}
          schools={schools}
          defaultSchoolId={ex.school_id}
          onCancel={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            onSaved();
          }}
        />
      </div>
    );
  }
  const scope = ex.school_id
    ? schools.find((s) => s.id === ex.school_id)?.name || "Unknown school"
    : "All schools";
  const sameDay = ex.start_date === ex.end_date;
  return (
    <>
      <div
        data-testid={`session-exception-row-${ex.id}`}
        className="grid grid-cols-[100px_220px_1fr_120px_60px] gap-2 px-3 py-2 items-center text-[11px]"
      >
        <span className="text-[10px] uppercase tracking-wider text-[#5C7088]">{ex.kind}</span>
        <span className="text-[#1F2937] tabular-nums">
          {sameDay ? ex.start_date : `${ex.start_date} → ${ex.end_date}`}
        </span>
        <span className="text-[#4B5563] truncate">{ex.label || "—"}</span>
        <span className="text-[#6B7280] truncate text-[10px]">{scope}</span>
        <div className="flex justify-end gap-1">
          <button
            onClick={() => setEditing(true)}
            data-testid={`session-exception-edit-${ex.id}`}
            className="text-[#6B7280] hover:text-[#1E3F33] p-1"
          >
            <Pencil size={11} />
          </button>
          <button
            onClick={() => setConfirmingDelete(true)}
            data-testid={`session-exception-delete-${ex.id}`}
            className="text-[#6B7280] hover:text-[#B85C5C] p-1"
          >
            <Trash size={11} />
          </button>
        </div>
      </div>
      <AlertDialog open={confirmingDelete} onOpenChange={setConfirmingDelete}>
        <AlertDialogContent data-testid="session-exception-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete calendar exception?</AlertDialogTitle>
            <AlertDialogDescription>
              This will resume scheduling sessions on{" "}
              <span className="font-semibold">
                {sameDay ? ex.start_date : `${ex.start_date} → ${ex.end_date}`}
              </span>{" "}
              ({ex.label || ex.kind}). Cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="session-exception-delete-cancel"
              disabled={deleting}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={remove}
              disabled={deleting}
              data-testid="session-exception-delete-confirm"
              className="bg-[#B85C5C] hover:bg-[#9b4747] text-white"
            >
              {deleting ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function SessionExceptionEditor({ existing, schools, defaultSchoolId, onCancel, onSaved }) {
  const [draft, setDraft] = useState(
    existing
      ? { ...existing }
      : {
          kind: "holiday",
          label: "",
          start_date: "",
          end_date: "",
          school_id: defaultSchoolId || "",
        }
  );
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!draft.label.trim()) return toast.error("Label required");
    if (!draft.start_date) return toast.error("Start date required");
    if (!draft.end_date) return toast.error("End date required");
    if (draft.end_date < draft.start_date)
      return toast.error("End date must be on or after start date");
    setSaving(true);
    try {
      const payload = {
        kind: draft.kind,
        label: draft.label.trim(),
        start_date: draft.start_date,
        end_date: draft.end_date,
        school_id: draft.school_id || null,
      };
      if (existing?.id) {
        await api.updateSessionException(existing.id, payload);
      } else {
        await api.createSessionException(payload);
      }
      toast.success(existing ? "Updated" : "Added");
      onSaved();
    } catch (e) {
      toast.error(`Save failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
    }
  };
  const invalidRange =
    draft.start_date && draft.end_date && draft.end_date < draft.start_date;
  return (
    <div
      data-testid="session-exception-editor"
      className="grid grid-cols-1 md:grid-cols-[100px_140px_140px_1fr_140px_auto] gap-1.5 items-center"
    >
      <select
        value={draft.kind}
        onChange={(e) => setDraft({ ...draft, kind: e.target.value })}
        data-testid="session-exception-kind"
        className="h-7 text-[11px] border border-[#d4d2cc] rounded-md px-1 bg-white"
      >
        {nativeOptions(SE_KIND_OPTIONS, (o) => ({ value: o.value, label: o.label, key: o.value }))}
      </select>
      <Input
        type="date"
        value={draft.start_date}
        onChange={(e) => setDraft({ ...draft, start_date: e.target.value })}
        data-testid="session-exception-start"
        className="h-7 text-[11px]"
      />
      <Input
        type="date"
        value={draft.end_date}
        onChange={(e) => setDraft({ ...draft, end_date: e.target.value })}
        data-testid="session-exception-end"
        className="h-7 text-[11px]"
      />
      <Input
        value={draft.label}
        onChange={(e) => setDraft({ ...draft, label: e.target.value })}
        placeholder="Spring Break, MLK Day, etc."
        data-testid="session-exception-label"
        className="h-7 text-[11px]"
      />
      <select
        value={draft.school_id || ""}
        onChange={(e) => setDraft({ ...draft, school_id: e.target.value })}
        data-testid="session-exception-school"
        className="h-7 text-[11px] border border-[#d4d2cc] rounded-md px-1 bg-white"
      >
        <option value="">All schools</option>
        {nativeOptions(schools, (s) => ({ value: s.id, key: s.id, label: s.name }))}
      </select>
      <div className="flex items-center gap-1 justify-end">
        <Button
          size="sm"
          onClick={save}
          disabled={saving || invalidRange || !draft.label.trim() || !draft.start_date || !draft.end_date}
          data-testid="session-exception-save"
          className="bg-[#1E3F33] hover:bg-[#173127] text-white rounded-md h-7 text-[11px] px-2 disabled:opacity-40"
        >
          <FloppyDisk size={11} weight="bold" className="mr-1" />
          {saving ? "…" : invalidRange ? "End < start" : "Save"}
        </Button>
        <button
          onClick={onCancel}
          data-testid="session-exception-cancel"
          className="text-[#6B7280] hover:text-[#1F2937] p-1"
        >
          <X size={12} />
        </button>
      </div>
    </div>
  );
}

